import { useEffect, useMemo, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { api, type LocalesResponse } from "../api";
import CountryModal from "../components/CountryModal";
import ConfirmDialog from "../components/ConfirmDialog";
import ImageLightbox from "../components/ImageLightbox";
import { useToast } from "../components/Toast";
import type { Category, Entry } from "../../shared/types";

export default function Countries() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [cfg, setCfg] = useState<LocalesResponse | null>(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState<string>("");
  const [editing, setEditing] = useState<Entry | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Entry | null>(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState<{ src: string; alt: string } | null>(null);
  const toast = useToast();

  useEffect(() => {
    let alive = true;
    Promise.all([api.listEntries(), api.getLocales(), api.listCategories()])
      .then(([e, c, cs]) => {
        if (!alive) return;
        setEntries(e);
        setCfg(c);
        setCats(cs);
        setLoading(false);
      })
      .catch((err) => toast.show("error", `Load failed: ${err.message}`));
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cat = catFilter ? cats.find((c) => c.cat_id === catFilter) : null;
    const allowed = cat ? new Set(cat.countries) : null;
    return entries.filter((e) => {
      if (allowed && !allowed.has(e.id)) return false;
      if (!q) return true;
      return (
        e.id.toLowerCase().includes(q) ||
        (e.titles.en ?? "").toLowerCase().includes(q)
      );
    });
  }, [entries, search, catFilter, cats]);

  async function reload() {
    const [e, c, cs] = await Promise.all([
      api.listEntries(),
      api.getLocales(),
      api.listCategories(),
    ]);
    setEntries(e);
    setCfg(c);
    setCats(cs);
  }

  async function handleSaveCreate(form: FormData) {
    try {
      await api.createEntry(form);
      toast.show("success", "Country created");
      setCreating(false);
      await reload();
    } catch (err) {
      toast.show("error", (err as Error).message);
    }
  }

  async function handleSaveEdit(form: FormData) {
    if (!editing) return;
    try {
      await api.updateEntry(editing.id, form);
      toast.show("success", "Country updated");
      setEditing(null);
      await reload();
    } catch (err) {
      toast.show("error", (err as Error).message);
    }
  }

  async function handleConfirmDelete() {
    if (!deleting) return;
    try {
      await api.deleteEntry(deleting.id);
      toast.show("success", "Country deleted");
      setDeleting(null);
      await reload();
    } catch (err) {
      toast.show("error", (err as Error).message);
    }
  }

  if (loading || !cfg) return <div className="text-gh-muted">Loading…</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="search id or EN title"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 rounded border border-gh-border text-sm flex-1 max-w-md"
        />
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="px-2 py-1.5 rounded border border-gh-border text-sm"
        >
          <option value="">All categories</option>
          {cats.map((c) => (
            <option key={c.cat_id} value={c.cat_id}>
              {c.cat_id} ({c.countries.length})
            </option>
          ))}
        </select>
        <button
          onClick={() => setCreating(true)}
          className="px-3 py-1.5 rounded bg-gh-success text-white text-sm flex items-center gap-1 hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> New country
        </button>
      </div>

      <table className="w-full bg-gh-surface border border-gh-border rounded text-sm">
        <thead className="bg-gh-bg text-left text-xs text-gh-muted">
          <tr>
            <th className="px-3 py-2 w-16">flag</th>
            <th className="px-3 py-2 w-16">emblem</th>
            <th className="px-3 py-2 w-16">map</th>
            <th className="px-3 py-2">id</th>
            <th className="px-3 py-2">EN title</th>
            <th className="px-3 py-2">EN capital</th>
            <th className="px-3 py-2 w-24"></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((e) => (
            <tr key={e.id} className="border-t border-gh-border">
              <td className="px-3 py-2">
                <button
                  type="button"
                  onClick={() => setZoom({ src: `/${e.images.flag}`, alt: `${e.id} flag` })}
                  className="block p-0 border-0 bg-transparent cursor-zoom-in"
                >
                  <img src={`/${e.images.flag}`} alt={`${e.id} flag`} className="w-12 h-9 object-contain" />
                </button>
              </td>
              <td className="px-3 py-2">
                {e.images.emblem ? (
                  <button
                    type="button"
                    onClick={() => setZoom({ src: `/${e.images.emblem}`, alt: `${e.id} emblem` })}
                    className="block p-0 border-0 bg-transparent cursor-zoom-in"
                  >
                    <img src={`/${e.images.emblem}`} alt={`${e.id} emblem`} className="w-12 h-9 object-contain" />
                  </button>
                ) : (
                  <span className="text-gh-muted">—</span>
                )}
              </td>
              <td className="px-3 py-2">
                {e.images.map ? (
                  <button
                    type="button"
                    onClick={() => setZoom({ src: `/${e.images.map}`, alt: `${e.id} map` })}
                    className="block p-0 border-0 bg-transparent cursor-zoom-in"
                  >
                    <img src={`/${e.images.map}`} alt={`${e.id} map`} className="w-12 h-9 object-contain" />
                  </button>
                ) : (
                  <span className="text-gh-muted">—</span>
                )}
              </td>
              <td className="px-3 py-2 font-mono text-xs">{e.id}</td>
              <td className="px-3 py-2">{e.titles.en}</td>
              <td className="px-3 py-2">{e.capitals.en}</td>
              <td className="px-3 py-2">
                <div className="flex gap-2">
                  <button onClick={() => setEditing(e)} className="text-gh-muted hover:text-gh-accent">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDeleting(e)} className="text-gh-muted hover:text-gh-danger">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {creating && cfg && (
        <CountryModal
          mode="create"
          entry={null}
          defaultLocale={cfg.default}
          onSave={handleSaveCreate}
          onCancel={() => setCreating(false)}
        />
      )}
      {editing && cfg && (
        <CountryModal
          mode="edit"
          entry={editing}
          defaultLocale={cfg.default}
          onSave={handleSaveEdit}
          onCancel={() => setEditing(null)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.id}?`}
        body="Removes the entry from all categories, deletes 3 PNGs. Snapshots saved in _cms/backups/."
        confirmLabel="Delete"
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
      {zoom && (
        <ImageLightbox
          src={zoom.src}
          alt={zoom.alt}
          onClose={() => setZoom(null)}
        />
      )}
    </div>
  );
}
